// The app's nutrition "knowledge layer": a registry of sources, structured evidence and the
// product rules that cite them. Nutrition logic in the app must point at a rule here; a rule must
// point at evidence. This keeps every claim traceable and reviewable.
//
// It is NOT a validated medical expert system. It makes product decisions evidence-informed.
//
// Review process: `npm run evidence:review` lists rules past their review date and evidence that
// was only read as a search summary. Update evidence.json from the primary source, then bump
// `reviewBy`.

import evidenceJson from './evidence.json';

export type EvidenceStrength = 'established' | 'estimate' | 'emerging';
export type Access = 'fetched' | 'snippet';

export interface Evidence {
  id: string;
  title: string;
  publisher: string;
  url: string;
  published_or_revised: string | null;
  accessed: string;
  /** 'snippet' means the primary document has not yet been read in full. */
  access: Access;
  evidence_type: string;
  claims: { claim: string; figure: string | null }[];
  product_rules: string[];
  strength: EvidenceStrength;
}

export const EVIDENCE: Evidence[] = evidenceJson as Evidence[];
const BY_ID = new Map(EVIDENCE.map((e) => [e.id, e]));

export function evidence(id: string): Evidence {
  const e = BY_ID.get(id);
  if (!e) throw new Error(`Unknown evidence ${id}`);
  return e;
}

export type RuleStatus =
  /** Directly supported by the cited evidence. */
  | 'evidence'
  /** A cautious product decision informed by the evidence, not dictated by it. */
  | 'product_decision';

export type Topic = 'protein' | 'plate' | 'variety' | 'weight' | 'safety' | 'activity' | 'data' | 'language';

export interface ProductRule {
  id: string;
  topic: Topic;
  /** What the app does. */
  rule: string;
  /** Plain-language explanation the app may show. No em dashes, no outcome promises. */
  userText: string;
  sources: string[];
  status: RuleStatus;
  version: number;
  reviewBy: string;
}

export const RULES: ProductRule[] = [
  {
    id: 'balanced_plate', topic: 'plate', version: 1, reviewBy: '2027-04-01', status: 'evidence',
    rule: 'For balanced, quality and maintenance goals, favour mains that combine a protein food, at least one vegetable and a grain or starch.',
    userText: 'Public guidelines describe a balanced plate as a mix of protein foods, vegetables and grains.',
    sources: ['dga_2025_2030', 'and_total_diet_approach'],
  },
  {
    id: 'vegetable_variety', topic: 'variety', version: 1, reviewBy: '2027-04-01', status: 'evidence',
    rule: 'Count distinct vegetables per meal and favour variety across the week. Look at the week, not single meals.',
    userText: 'Guidelines suggest several servings of vegetables a day, so we favour meals with more of them across your week.',
    sources: ['dga_2025_2030'],
  },
  {
    id: 'protein_anchor', topic: 'protein', version: 1, reviewBy: '2027-04-01', status: 'evidence',
    rule: 'For muscle, performance and weight-gain goals, favour mains built around a protein food. Food first; never suggest supplements.',
    userText: 'Research on resistance training links muscle gain to enough daily protein alongside training. We favour meals built around a protein food.',
    sources: ['jager_2017_issn_protein', 'morton_2018_bjsm'],
  },
  {
    id: 'protein_reference_range', topic: 'protein', version: 1, reviewBy: '2027-04-01', status: 'evidence',
    rule: 'Only when the user opted into a muscle or performance goal and gave a body weight, show the published reference range of 1.4 to 2.0 g per kg per day as context, labelled as a reference, never as a target.',
    userText: 'A sports nutrition position stand gives 1.4 to 2.0 g of protein per kg of body weight per day as a reference range for people building muscle. A meta-analysis found extra gains levelled off around 1.6 g per kg on average.',
    sources: ['jager_2017_issn_protein', 'morton_2018_bjsm'],
  },
  {
    id: 'no_weight_predictions', topic: 'weight', version: 1, reviewBy: '2027-04-01', status: 'evidence',
    rule: 'Never predict future weight or goal dates. Food logs cannot predict an individual weight change; the 3,500 kcal rule overestimates change.',
    userText: 'We never predict your weight. Simple calorie maths overestimates change, and bodies adapt.',
    sources: ['hall_2011_lancet', 'nih_body_weight_planner'],
  },
  {
    id: 'gradual_pace', topic: 'weight', version: 1, reviewBy: '2027-04-01', status: 'evidence',
    rule: 'For weight-loss goals, describe gradual change only, never set calorie targets or aggressive paces.',
    userText: 'Health agencies describe gradual, steady change as the pace most people keep. We focus on balanced, filling meals rather than targets.',
    sources: ['cdc_steps_losing_weight', 'niddk_safe_weight_loss_program'],
  },
  {
    id: 'no_calorie_targets', topic: 'safety', version: 1, reviewBy: '2027-04-01', status: 'product_decision',
    rule: 'Do not set calorie targets, streaks or restriction badges. Offer a setting that hides all nutrition and body numbers.',
    userText: 'We don’t set calorie targets or streaks. You can hide all numbers in Settings.',
    sources: ['levinson_2017_mfp_eating_disorders', 'neda_warning_signs_resources'],
  },
  {
    id: 'support_resources', topic: 'safety', version: 1, reviewBy: '2027-04-01', status: 'product_decision',
    rule: 'Keep a visible link to eating-disorder support and suggest talking to a clinician or registered dietitian for medical needs.',
    userText: 'If food or weight feels stressful, support is available. For medical needs, talk to a doctor or a registered dietitian.',
    sources: ['neda_warning_signs_resources'],
  },
  {
    id: 'no_food_morality', topic: 'language', version: 1, reviewBy: '2027-04-01', status: 'evidence',
    rule: 'Never label foods good, bad, clean, junk or cheat. Talk about overall patterns.',
    userText: 'All foods can fit. We look at your overall pattern, not single meals.',
    sources: ['and_total_diet_approach'],
  },
  {
    id: 'no_regulated_claims', topic: 'language', version: 1, reviewBy: '2027-04-01', status: 'product_decision',
    rule: 'Avoid regulated nutrient-content terms such as "high protein" or "good source" unless computed to FDA thresholds. Use descriptive wording instead.',
    userText: 'We describe meals ("built around chicken") rather than using label claims.',
    sources: ['fda_nutrient_content_claims'],
  },
  {
    id: 'activity_context', topic: 'activity', version: 1, reviewBy: '2027-04-01', status: 'evidence',
    rule: 'Activity questions are optional and only tune which meals rank higher. Never add exercise calories back or frame food as earned.',
    userText: 'Activity only helps us suggest meals. We never ask you to earn your food.',
    sources: ['pag_2nd_edition'],
  },
  {
    id: 'nutrient_data_source', topic: 'data', version: 1, reviewBy: '2027-04-01', status: 'evidence',
    rule: 'Nutrient values come only from USDA FoodData Central records (with fdcId). Show estimates with a range and label them as estimates. If coverage is incomplete, show nothing.',
    userText: 'Nutrition estimates use USDA FoodData Central and are approximate: brands, portions and cooking change the real numbers.',
    sources: ['usda_fooddata_central'],
  },
];

const RULE_BY_ID = new Map(RULES.map((r) => [r.id, r]));

export function rule(id: string): ProductRule {
  const r = RULE_BY_ID.get(id);
  if (!r) throw new Error(`Unknown rule ${id}`);
  return r;
}

/** Retrieval for explanations (and any future model prompt): rules plus their evidence for a topic. */
export function retrieve(topic: Topic): { rule: ProductRule; evidence: Evidence[] }[] {
  return RULES.filter((r) => r.topic === topic).map((r) => ({ rule: r, evidence: r.sources.map(evidence) }));
}

/** Rules whose review date has passed or that rest on summary-only evidence. */
export function needsReview(today: string): { rule: ProductRule; reasons: string[] }[] {
  return RULES.flatMap((r) => {
    const reasons: string[] = [];
    if (r.reviewBy <= today) reasons.push(`review date ${r.reviewBy} passed`);
    const snippets = r.sources.filter((s) => evidence(s).access === 'snippet');
    if (snippets.length) reasons.push(`evidence read as summary only: ${snippets.join(', ')}`);
    return reasons.length ? [{ rule: r, reasons }] : [];
  });
}
